import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { ApiClient } from '@/services/api/client'
import { callWithAuth } from '@/services/auth/call-with-auth'
import { LoadSessionData } from '@/services/session/load-session-data'
import { log } from '@/util/log'
import { Either, left, right } from 'fp-ts/lib/Either'

export class UsualPowerService {
  constructor (
    private readonly apiClient: typeof ApiClient = ApiClient
  ) {}
  /**
   * @param options.noCache このエンドポイントは `private, max-age=30` のブラウザキャッシュが効く
   *                        （`config/routes.php` の `cache-as-private`）。保存直後など鮮度が必要な
   *                        場面で指定すると、キャッシュされたエントリを使わずに取り直す
   */
  async getUserInfo(options: { noCache?: boolean } = {}): Promise<Either<Error, UsualPower>> {
    const target = `/api/user/usual_power/current_user`

    try {
      const response = await callWithAuth(() => {
        const request = this.apiClient.get(target)

        return options.noCache ? request.set({ 'Cache-Control': 'no-cache' }) : request
      })

      log.debug({ service: target, response })

      return right(response.body.data)
    } catch (error) {
      log.error({ service: target, error })

      return left(error)
    }
  }
  async update(leftPathname: string, rightPathname: string): Promise<Either<Error, null>> {
    const target = `/api/user/usual_power/update`

    try {
      const response = await callWithAuth(() => this.apiClient
        .post(target, {
          leftPathname,
          rightPathname,
        }))

      log.debug({ service: target, response })

      // 保存は別URLへのPOSTでブラウザキャッシュが無効化されないため、取り直して古いエントリを置き換える
      await this.getUserInfo({ noCache: true })

      return right(response.body)
    } catch (error) {
      log.error({ service: target, error })

      return left(error)
    }
  }
}

/**
 * left/rightPathnameのundefinedは「設定無し」を意味する
 * 「度なしとして設定済み」の場合、度なしのpathnameが入る
 */
export interface UsualPower {
  leftPathname?: string,
  rightPathname?: string,
  /**
   * ホテラバアプリBFFへの移行時にアプリとWebで度数が食い違い、度数がリセットされたことを示す。
   * APIは常にこのフィールドを返す（リセットが起こり得ない環境では false）
   */
  migrationConflicted: boolean
}

interface CreateUsualPowerServiceDep {
  getIsConfigEnabled: (brandName: string | null) => boolean
  getIsRegisteredUser: () => Promise<boolean>
}
const defaultUsualPowerServiceDep: CreateUsualPowerServiceDep = {
  getIsConfigEnabled: (brandName: string | null) => BehaviorConfig.createFromBrand(brandName).usualPower.enabled,
  /*
   * 本会員判定は検証済みセッションデータの本会員フラグを使う。
   * s cookie は廃止方向のため判定に使わない（docs/policy/architecture/auth-and-session.md）
   */
  getIsRegisteredUser: () => LoadSessionData.getInstance().load()
    .then(payload => payload.data.isRegistered),
}

/**
 * いつもの度数の取得サービスを作る。機能が無効なホストでは通信しないスタブを返す。
 *
 * getUserInfo の戻り値の契約:
 * - right: 度数の状態が確定している。未ログインは「設定無し」が正しい状態なので right に含める
 * - left: 度数が分からない（取得に失敗し、未ログインとも確定できない）
 *
 * ログイン状態は取得失敗時の解釈（left か「設定無し」の right か）にだけ必要なので、取得を試みた後に判定する。
 */
export function createUsualPowerGetService(
  brandEnglishName: string | null,
  dep: CreateUsualPowerServiceDep = defaultUsualPowerServiceDep
): Pick<UsualPowerService, 'getUserInfo'> {
  if (!dep.getIsConfigEnabled(brandEnglishName)) {
    log.debug('create disabled usual power service')

    return { getUserInfo: async () => right({ migrationConflicted: false }) }
  }

  const service = new UsualPowerService()

  return {
    getUserInfo: async (options) => {
      const result = await service.getUserInfo(options)
      if (!result.isLeft()) {
        return result
      }

      /*
       * 未ログインと分かったときだけ right に落とす。判定そのものができないときは left を維持する。
       * 判定不能を未ログイン扱いにすると、本会員でも「設定無し」が確定したことになってしまう。
       */
      const isRegistered = await dep.getIsRegisteredUser().catch(() => true)

      return isRegistered ? result : right({ migrationConflicted: false })
    },
  }
}
